
import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';

interface SelectionContextType {
  selectedIds: Set<string>;
  lastSelectedId: string | null;
  toggleSelection: (id: string, shiftKey?: boolean, allVisibleIds?: string[]) => void;
  clearSelection: () => void;
  setSelection: (ids: Set<string>) => void;
  isSelected: (id: string) => boolean;
  count: number;
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

export const SelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const toggleSelection = useCallback((id: string, shiftKey?: boolean, allVisibleIds?: string[]) => {
    if (shiftKey && lastSelectedId && allVisibleIds) {
        // Range Selection Logic
        const startIdx = allVisibleIds.indexOf(lastSelectedId);
        const endIdx = allVisibleIds.indexOf(id);
        
        if (startIdx !== -1 && endIdx !== -1) {
            const minIdx = Math.min(startIdx, endIdx);
            const maxIdx = Math.max(startIdx, endIdx);
            const range = allVisibleIds.slice(minIdx, maxIdx + 1);
            
            setSelectedIds(prev => {
                const newSet = new Set(prev);
                range.forEach(itemId => newSet.add(itemId));
                return newSet;
            });
        }
    } else {
        // Standard Toggle Logic
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          if (newSet.has(id)) newSet.delete(id);
          else newSet.add(id);
          return newSet;
        });
        setLastSelectedId(id);
    }
  }, [lastSelectedId]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const setSelection = useCallback((ids: Set<string>) => {
    setSelectedIds(ids);
    setLastSelectedId(null); 
  }, []);
  
  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  return (
    <SelectionContext.Provider value={{ 
      selectedIds, 
      lastSelectedId, 
      toggleSelection, 
      clearSelection, 
      setSelection,
      isSelected,
      count: selectedIds.size
    }}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};
